import { useState } from "react";
import type { LocationSummary, RuleSet } from "../../api/contracts";
import { windowPhrase, type ScopeChoice } from "../lib/rules";
import { Button, PickBox, SidePanel } from "./ui";
import { PreviewResults, PreviewStatus, usePreview } from "./PreviewResults";

/** The locations a scope's rules reach: the one location, the owner's, or every location for the corporate default. */
export function locationsInScope(choice: ScopeChoice, all: LocationSummary[]): LocationSummary[] {
  if (choice.scope === "location") return all.filter((l) => l.blvdId === choice.key);
  if (choice.scope === "owner") return all.filter((l) => l.ownerIdentifier === choice.key);
  return all;
}

export const PREVIEW_PANEL_LABEL = "Preview with these rules";

/**
 * The Rules page's preview of its unsaved draft: pick a location the scope reaches, run the same
 * preview the Preview page runs, but with the rules as they are on the page. Nothing is saved.
 */
export function RulesPreviewPanel({ draft, choice, locations, admin, onClose }: {
  draft: RuleSet; choice: ScopeChoice; locations: LocationSummary[]; admin: boolean; onClose: () => void;
}) {
  const options = locationsInScope(choice, locations);
  const [blvdId, setBlvdId] = useState(options[0]?.blvdId ?? "");
  const preview = usePreview(blvdId, draft);
  const location = options.find((l) => l.blvdId === blvdId);
  const ownRules = choice.scope !== "location" && location?.ruleScope === "location";
  return (
    <SidePanel label={PREVIEW_PANEL_LABEL} title={PREVIEW_PANEL_LABEL} onClose={onClose} width="max-w-[900px]"
      intro={<p className="mt-2 max-w-[640px] text-sm font-medium text-hs-text-muted">What BTB blocks would do over {windowPhrase(draft.lookaheadDays)} with the rules as they are on this page, saved or not. Nothing is changed by running a preview.</p>}>
      <div className="flex flex-wrap items-end gap-3">
        {options.length > 1
          ? (
            <PickBox label="Location" className="min-w-[14rem]" value={blvdId} onChange={(e) => { preview.reset(); setBlvdId(e.target.value); }}>
              {options.map((l) => <option key={l.blvdId} value={l.blvdId}>{l.name}</option>)}
            </PickBox>
          )
          : <p className="text-base font-semibold text-hs-text">{location?.name ?? "No location in this scope"}</p>}
        <Button onClick={preview.start} disabled={!blvdId || preview.running}>{preview.running ? "Running…" : "Run preview"}</Button>
      </div>
      {ownRules && location && (
        <p className="mt-3 text-xs font-medium text-hs-text-muted">{location.name} has rules of its own, so saving these would not change it. The preview uses the rules on this page anyway.</p>
      )}
      <div className="mt-6">
        <PreviewStatus preview={preview} />
        <PreviewResults result={preview.result} locationName={location?.name} admin={admin} />
      </div>
    </SidePanel>
  );
}
